import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import { Box, InputAdornment, Menu, styled, TextField, type Theme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import type { Range } from "react-date-range";
import { CalendarButton } from "src/components/Button/CalendarButton";
import { CalendarRange } from "src/components/Selector/CalendarRange/CalendarRange";
import DateRange from "src/components/Selector/CalendarRange/DateRange";
import { RangeType } from "src/types/enum/RangeType";
import type { HeaderCalendar } from "src/types/offer/HeaderParameter";
import type { StaticCompareRange, StaticRange } from "src/types/staticRange";
import type { TimeRange } from "src/types/timeRange";
import { findCorrectRangeByDate } from "src/utils/timeRanges";
import { formatDate } from "../../utils/formatters/dates";
import { formatCalenderFilter } from "./hooks";

interface CalendarSelectProps {
    staticRanges?: StaticRange[] | StaticCompareRange[];
    handleSelect: (range: TimeRange) => void;
    defaultTimeRange?: TimeRange;
    headerCalendar?: HeaderCalendar;
    disableDay?: (date: Date) => boolean;
    displayLabel?: string;
    forceOpen?: boolean;
    disabled?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        margin: {
            margin: theme.spacing(1),
        },
        input: {
            marginTop: 2,
            fontSize: 12,
            width: 140,
            cursor: "pointer",
        },
        button: {
            "&:hover": {
                backgroundColor: "inherit",
            },
        },
        textField: {
            "& .MuiInputBase-root": {
                cursor: "pointer",
            },
        },
    })
);

const StyledTextField = styled(TextField)(() => ({
    marginTop: 2,
    width: "168px",
    cursor: "pointer",
    border: "none",
    "& .MuiInputBase-root": {
        fontWeight: 400,
        height: "20px",
        width: "180px",
        border: "none",
    },
    "& .MuiInputBase-input": {
        fontSize: "12px",
        padding: "10px",
        height: "100%",
        boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
    },
}));

const defaultDate: TimeRange = {
    startDate: new Date(),
    endDate: new Date(),
    key: RangeType.selection,
};

export const CalendarSelect = ({
    staticRanges,
    handleSelect,
    headerCalendar: { from, to } = {},
    defaultTimeRange = defaultDate,
    disableDay,
    displayLabel,
    forceOpen = false,
    disabled = false,
}: CalendarSelectProps): JSX.Element => {
    const classes = useStyles();
    const actionRef = useRef<any>(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const [state, setState] = useState<Range[]>([defaultTimeRange]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    useEffect(() => {
        if (forceOpen && anchorEl === null) {
            setAnchorEl(actionRef.current);
        }
    }, [forceOpen, anchorEl, setAnchorEl]);

    const handleClose = () => {
        setAnchorEl(null);
        handleSelect(state[0] as TimeRange);
    };

    const preventInputFocus = (event: any) => {
        event.preventDefault();
        if (event.relatedTarget) {
            // Revert focus back to previous blurring element
            event.relatedTarget.focus();
        } else {
            // No previous focus target, blur instead
            event.currentTarget.blur();
        }
    };

    useEffect(() => {
        const ranges = staticRanges?.map((e) => e.range()) ?? [];
        const correctRange = findCorrectRangeByDate(
            new Date(from),
            new Date(to),
            ranges,
            defaultTimeRange
        );
        if (from && to) {
            setState([
                {
                    startDate: moment(from).toDate(),
                    endDate: moment(to).toDate(),
                    key: correctRange.key,
                    id: correctRange.id,
                } as Range,
            ]);
        }
    }, [from, to]);

    return (
        <Box component="span">
            <CalendarButton
                handleClick={handleClick}
                ref={actionRef}
                disableRipple
                className={classes.button}
                disabled={disabled}
            >
                <StyledTextField
                    onFocus={preventInputFocus}
                    slotProps={{
                        htmlInput: { className: classes.input },
                        input: {
                            endAdornment: (
                                <InputAdornment position="end" sx={{ margin: 0 }}>
                                    <ArrowDropDown style={{ opacity: 0.7 }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                    value={displayAsString(state[0] as TimeRange, displayLabel)}
                    disabled={disabled}
                />
            </CalendarButton>
            <Menu
                anchorEl={anchorEl}
                onClose={() => handleClose()}
                open={Boolean(anchorEl)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
            >
                <div>
                    {staticRanges?.length > 0 ? (
                        <CalendarRange
                            staticRange={staticRanges}
                            handleSetState={(states) => setState(states)}
                            state={state}
                            disableFuture={disableDay ? disableDay : () => false}
                        />
                    ) : (
                        <DateRange
                            handleSetState={(states) => setState(states)}
                            state={state}
                            disableFuture={disableDay ? disableDay : () => false}
                        />
                    )}
                </div>
            </Menu>
        </Box>
    );
};

export function displayAsString(range: TimeRange, displayLabel?: string): string {
    if (displayLabel) {
        return displayLabel;
    }

    return formatCalenderFilter({
        from: formatDate(range.startDate),
        to: formatDate(range.endDate),
    });
}
